/* eslint-disable @typescript-eslint/no-explicit-any */

export interface Role { id: number; name: string; description?: string | null; is_system?: boolean; permissions?: any[]; permission_count?: number }
export interface Permission { id: number; name: string; key?: string; permission_key?: string; group?: string; section?: string }

// API envelopes vary ({data:[…]}, {data:{roles:[…]}}, […]) — find the array.
export function toArray(res: any, ...keys: string[]): any[] {
  const d = res?.data ?? res
  if (Array.isArray(d)) return d
  for (const k of keys) if (Array.isArray(d?.[k])) return d[k]
  return []
}

export const idsOf = (list: any[]) =>
  list.map((p: any) => p.permission_id ?? p.role_id ?? p.id ?? p.permission?.id ?? p.role?.id).filter(Boolean) as number[]

const METHOD_RE = /^(GET|POST|PUT|PATCH|DELETE)\s+(\S+)/i
const GQL_RE = /^GQL\s+(Query|Mutation)\.(\w+)/i

/** The backend identifies a permission by its key: 'GET /admin/…' or 'GQL Query.adminGetBookingList'. */
export const permKey = (p: any): string => String(p?.key ?? p?.permission_key ?? p?.name ?? '')

export interface PermView {
  id: number
  key: string        // what the RBAC API takes: 'GET /admin/booking/get-bookings-list'
  group: string      // e.g. "booking"
  label: string      // e.g. "Get bookings list"
  method?: string    // e.g. "GET" | "QUERY" | "MUTATION"
  raw: string        // original key, for the tooltip
}

const humanize = (s: string) => {
  const t = s.replace(/[-_]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim()
  return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : s
}

/** Turns "GET /admin/booking/get-bookings-list" into { group: 'booking', label: 'Get bookings list', method: 'GET' }. */
export function describePermission(p: Permission): PermView {
  const key = permKey(p)
  const gql = GQL_RE.exec(key)
  if (gql) {
    return { id: p.id, key, group: normalizeGroup(p.section ?? 'graphql'), label: cap(words(gql[2]).join(' ')), method: gql[1].toUpperCase(), raw: key }
  }
  const m = METHOD_RE.exec(key)
  if (!m) return { id: p.id, key, group: normalizeGroup(p.section ?? p.group ?? 'General'), label: key, raw: key }
  const method = m[1].toUpperCase()
  const segs = m[2].split('/').filter(Boolean).filter(s => s !== 'v2' && s !== 'admin' && !s.startsWith(':'))
  const group = normalizeGroup(p.section ?? p.group ?? segs[0] ?? 'General')
  return { id: p.id, key, group, label: friendlyLabel(method, segs.slice(1), group), method, raw: key }
}

const words = (s: string) =>
  s.replace(/([a-z])([A-Z])/g, '$1 $2').split(/[-_\s/]+/).map(w => w.toLowerCase()).filter(Boolean)

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

/** "trainerPendingRequest" / "all-in-one" → "Trainer pending request" / "All in one". */
export function friendlyGroup(g: string): string {
  if (g.toLowerCase() === 'graphql') return 'GraphQL (advanced)'
  return cap(words(g).join(' ')) || g
}

/** Plain-language action name: ('GET', ['get-bookings-list']) → "View bookings list". */
function friendlyLabel(method: string, segs: string[], group: string): string {
  const all = segs.flatMap(words)
  const has = (w: string) => all.includes(w)
  const noun = all.filter(w => !['get', 'create', 'update', 'delete', 'add', 'remove', 'by', 'id'].includes(w)).join(' ')
  const verb =
    method === 'DELETE' ? 'Delete'
    : method === 'GET' || has('get') ? 'View'
    : method === 'PUT' || method === 'PATCH' ? 'Change'
    : has('create') && has('update') ? 'Add or change'
    : has('update') ? 'Change'
    : 'Add'
  return `${verb} ${noun || words(group).join(' ')}`.trim()
}

export type Capability = 'view' | 'edit' | 'delete'
/** What kind of action a permission is. GraphQL mutations count as delete when their name says so. */
export function capabilityOf(v: { method?: string; key?: string }): Capability {
  if (v.method === 'GET' || v.method === 'QUERY') return 'view'
  if (v.method === 'DELETE') return 'delete'
  if (v.method === 'MUTATION' && /delete|remove/i.test(v.key ?? '')) return 'delete'
  return 'edit'
}

/**
 * One area per feature. Sections arrive as 'booking', 'booking v1', 'graphql:booking',
 * 'trainerPendingRequest' … — fold the REST and GraphQL variants of a feature into one area.
 */
export function normalizeGroup(raw: string): string {
  const base = raw
    .replace(/^graphql[:\s_-]*/i, '')
    .replace(/^v\d+[\s_-]+/i, '')
    .replace(/[\s_-]+v\d+$/i, '')
  const w = words(base).join(' ')
  return w || (/^graphql/i.test(raw) ? 'graphql' : raw.toLowerCase())
}

// ── Categories: ~140 technical areas are too many to scan, so file them under a few business headings ──
export interface Category { key: string; label: string; hint: string }
export const CATEGORIES: Category[] = [
  { key: 'bookings', label: 'Bookings & sessions', hint: 'Bookings, sessions, reassignment, tracking' },
  { key: 'people',   label: 'People & schools',    hint: 'Users, trainers, students, schools, documents' },
  { key: 'finance',  label: 'Money & payments',    hint: 'Payouts, payments, wallets, refunds, commissions' },
  { key: 'catalog',  label: 'Catalog & marketing', hint: 'Packages, products, discounts, sliders, reviews, notifications' },
  { key: 'reports',  label: 'Reports & analytics', hint: 'Reports and dashboards' },
  { key: 'system',   label: 'System & settings',   hint: 'Cities, languages, app settings, roles & access' },
  { key: 'other',    label: 'Other',               hint: 'Everything else' },
]

const CATEGORY_RULES: [string, RegExp][] = [
  ['reports',  /report|analytic/],
  ['finance',  /payout|payment|wallet|withdraw|cash|collection|earning|commission|revenue|price|refund|transaction/],
  ['bookings', /booking|session|reschedul|reassign|unattended|shown up|tracking|gps|schedule/],
  ['people',   /user|student|trainer|assistant|collector|school|vendor|auth|address|leave|working hours|doc|certificate|profile|nearest|radius/],
  ['catalog',  /package|all in one|category|product|discount|coupon|delivery|slider|home|listing|review|rating|reward|notification|message|support|recent search|signature/],
  ['system',   /city|state|dialect|language|app |^app|setting|version|permission|role|rbac|access|pickup|geography|config|graphql/],
]

export function categoryOf(group: string): string {
  for (const [key, re] of CATEGORY_RULES) if (re.test(group)) return key
  return 'other'
}

export const METHOD_STYLE: Record<string, string> = {
  QUERY: 'bg-blue-50 text-blue-700',
  MUTATION: 'bg-amber-50 text-amber-700',
  GET: 'bg-blue-50 text-blue-700',
  POST: 'bg-green-50 text-green-700',
  PUT: 'bg-amber-50 text-amber-700',
  PATCH: 'bg-amber-50 text-amber-700',
  DELETE: 'bg-red-50 text-red-700',
}

// ── Access levels: one simple choice per area instead of ticking every endpoint ──
export type Level = 'none' | 'view' | 'edit' | 'full'

export const LEVELS: { key: Level; label: string; hint: string }[] = [
  { key: 'none', label: 'None', hint: 'No access to this area' },
  { key: 'view', label: 'View', hint: 'Can look at information only' },
  { key: 'edit', label: 'Edit', hint: 'Can view, add and change — but not delete' },
  { key: 'full', label: 'Full', hint: 'Can do everything, including delete' },
]


/** Permission ids in `items` that a given level grants. */
export function idsForLevel(items: PermView[], level: Level): number[] {
  if (level === 'none') return []
  if (level === 'full') return items.map(i => i.id)
  const allowed: Capability[] = level === 'view' ? ['view'] : ['view', 'edit']
  return items.filter(i => allowed.includes(capabilityOf(i))).map(i => i.id)
}

/** Which level the current selection matches for an area, or 'custom' if hand-picked. */
export function levelOf(items: PermView[], draft: Set<number>): Level | 'custom' {
  const on = new Set(items.filter(i => draft.has(i.id)).map(i => i.id))
  if (on.size === 0) return 'none'
  for (const l of ['view', 'edit', 'full'] as Level[]) {
    if (sameSet(on, new Set(idsForLevel(items, l)))) return l
  }
  return 'custom'
}

export const sameSet = (a: Set<number>, b: Set<number>) => a.size === b.size && [...a].every(x => b.has(x))
